<script setup>
import { ref, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Modal from 'dashboard/components/Modal.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import MessageAPI from 'dashboard/api/inbox/message';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversation: { type: Object, default: null },
});

const emit = defineEmits(['close']);

const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');
const inboxesList = useMapGetter('inboxes/getInboxes');

const messages = ref([]);
const isLoading = ref(false);

const localShow = computed({
  get: () => props.show,
  set: val => {
    if (!val) emit('close');
  },
});

const contact = computed(() => props.conversation?.meta?.sender || {});
const assignee = computed(() => props.conversation?.meta?.assignee || null);

const inbox = computed(() => {
  if (!props.conversation?.inbox_id) return null;
  return (
    inboxesList.value.find(i => i.id === props.conversation.inbox_id) || null
  );
});

const STATUS_LABEL = {
  open: 'Aberta',
  resolved: 'Resolvida',
  pending: 'Pendente',
  snoozed: 'Adiada',
};

const STATUS_CLASS = {
  open: 'bg-n-teal-3 text-n-teal-11',
  resolved: 'bg-n-slate-3 text-n-slate-11',
  pending: 'bg-n-amber-3 text-n-amber-11',
  snoozed: 'bg-n-slate-3 text-n-slate-11',
};

const statusLabel = computed(
  () =>
    STATUS_LABEL[props.conversation?.status] || props.conversation?.status || ''
);
const statusClass = computed(
  () =>
    STATUS_CLASS[props.conversation?.status] || 'bg-n-slate-3 text-n-slate-11'
);

const createdAt = computed(() => {
  if (!props.conversation?.created_at) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(props.conversation.created_at * 1000));
});

const fmtTime = ts => {
  if (!ts) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  }).format(new Date(ts * 1000));
};

const ATTACHMENT_ICONS = {
  image: 'i-lucide-image',
  audio: 'i-lucide-headphones',
  video: 'i-lucide-video',
  file: 'i-lucide-paperclip',
};

const attachmentIcon = type => ATTACHMENT_ICONS[type] || 'i-lucide-paperclip';

const loadMessages = async () => {
  if (!props.conversation?.id) return;
  isLoading.value = true;
  messages.value = [];
  try {
    const { data } = await MessageAPI.getPreviousMessages({
      conversationId: props.conversation.id,
    });
    messages.value = (data.payload || [])
      .filter(m => m.message_type !== 2)
      .sort((a, b) => a.id - b.id);
  } catch {
    // silent
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.conversation?.id,
  id => {
    if (id) loadMessages();
  },
  { immediate: true }
);

const openFullConversation = () => {
  const path = frontendURL(
    conversationUrl({ accountId: accountId.value, id: props.conversation.id })
  );
  router.push({ path });
  emit('close');
};

const isResolved = computed(() => props.conversation?.status === 'resolved');
const contactId = computed(() => String(contact.value?.id || ''));

const isOutgoing = msg => msg.message_type === 1;
</script>

<template>
  <Modal
    v-model:show="localShow"
    :on-close="() => emit('close')"
    @close="emit('close')"
  >
    <div class="flex flex-col max-h-[85vh] min-h-[30vh] w-full">
      <!-- Header -->
      <div class="px-6 pt-6 pb-4 border-b border-n-weak flex-shrink-0">
        <div class="flex items-start justify-between gap-3 pr-8">
          <div class="flex flex-col gap-1 min-w-0">
            <span class="text-base font-semibold text-n-slate-12 truncate">
              {{ contact.name || '—' }}
            </span>
            <div
              class="flex items-center flex-wrap gap-2 text-xs text-n-slate-11"
            >
              <span v-if="inbox" class="flex items-center gap-1">
                <span class="i-lucide-inbox size-3" />
                {{ inbox.name }}
              </span>
              <span v-if="assignee" class="flex items-center gap-1">
                <span class="i-lucide-user size-3" />
                {{ assignee.name }}
              </span>
              <span v-if="createdAt" class="flex items-center gap-1">
                <span class="i-lucide-calendar size-3" />
                {{ createdAt }}
              </span>
              <span
                v-if="statusLabel"
                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                :class="statusClass"
              >
                {{ statusLabel }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Messages -->
      <div
        class="flex-1 overflow-y-auto px-4 py-4 min-h-0 space-y-3 bg-n-slate-1"
      >
        <div v-if="isLoading" class="flex items-center justify-center py-10">
          <Spinner />
        </div>
        <div
          v-else-if="!messages.length"
          class="flex items-center justify-center py-10 text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION_PREVIEW_MODAL.NO_MESSAGES') }}
        </div>
        <template v-else>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="flex"
            :class="isOutgoing(msg) ? 'justify-end' : 'justify-start'"
          >
            <div
              class="max-w-[75%] flex flex-col gap-1"
              :class="isOutgoing(msg) ? 'items-end' : 'items-start'"
            >
              <span class="text-xxs text-n-slate-10 px-1">
                {{
                  msg.sender?.name || (isOutgoing(msg) ? 'Agente' : 'Contato')
                }}
              </span>
              <div
                class="rounded-xl px-3 py-2 text-sm leading-relaxed"
                :class="{
                  'bg-n-teal-9 text-white': isOutgoing(msg) && !msg.private,
                  'bg-n-slate-4 text-n-slate-12':
                    !isOutgoing(msg) && !msg.private,
                  'bg-n-amber-2 text-n-amber-11 border border-n-amber-4':
                    msg.private,
                }"
              >
                <div
                  v-if="msg.private"
                  class="flex items-center gap-1 text-xxs font-medium mb-1 text-n-amber-10"
                >
                  <span class="i-lucide-lock size-3" />
                  {{ $t('CONVERSATION_PREVIEW_MODAL.PRIVATE_NOTE') }}
                </div>
                <p
                  v-if="msg.content"
                  class="whitespace-pre-wrap break-words mb-0"
                >
                  {{ msg.content }}
                </p>
                <div
                  v-if="msg.attachments && msg.attachments.length"
                  class="mt-1 flex flex-col gap-1"
                >
                  <div
                    v-for="att in msg.attachments"
                    :key="att.id"
                    class="flex items-center gap-1.5 text-xs opacity-80"
                  >
                    <span
                      :class="attachmentIcon(att.file_type)"
                      class="size-3 flex-shrink-0"
                    />
                    <span class="truncate">{{
                      att.data_url?.split('/').pop() || 'Anexo'
                    }}</span>
                  </div>
                </div>
              </div>
              <span class="text-xxs text-n-slate-10 px-1">{{
                fmtTime(msg.created_at)
              }}</span>
            </div>
          </div>
        </template>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-n-weak flex-shrink-0 flex items-center justify-end gap-3"
      >
        <span class="text-xs text-n-slate-10">
          {{ $t('CONVERSATION_PREVIEW_MODAL.READ_ONLY') }}
        </span>
        <button
          v-if="!isResolved"
          type="button"
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10 transition-colors"
          @click="openFullConversation"
        >
          <span class="i-lucide-external-link size-4" />
          {{ $t('CONVERSATION_PREVIEW_MODAL.OPEN_CONVERSATION') }}
        </button>
        <ComposeConversation
          v-else-if="contactId"
          :contact-id="contactId"
          is-modal
          @close="emit('close')"
        >
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-teal-9 text-white hover:bg-n-teal-10 transition-colors"
              @click="toggle"
            >
              <span class="i-lucide-message-square-plus size-4" />
              {{ $t('CONVERSATION_PREVIEW_MODAL.NEW_CONVERSATION') }}
            </button>
          </template>
        </ComposeConversation>
      </div>
    </div>
  </Modal>
</template>
